import { useCallback, useId, useState } from "react";

export interface DisclosureTriggerProps {
  "aria-expanded": boolean;
  "aria-controls": string;
  onClick: () => void;
  type: "button";
}

export interface DisclosurePanelProps {
  id: string;
}

export interface Disclosure {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  triggerProps: DisclosureTriggerProps;
  panelProps: DisclosurePanelProps;
}

/**
 * Keep the trigger mounted while the panel is open. A control that unmounts itself on click drops
 * keyboard focus to <body> (WCAG 2.4.3).
 */
export function useDisclosure(initiallyOpen = false): Disclosure {
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const panelId = useId();

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((wasOpen) => !wasOpen), []);

  return {
    isOpen,
    open,
    close,
    toggle,
    triggerProps: {
      "aria-expanded": isOpen,
      "aria-controls": panelId,
      onClick: toggle,
      type: "button"
    },
    panelProps: { id: panelId }
  };
}

export default useDisclosure;
